'use client';

import type { ReactElement, ReactNode } from 'react';

import { Collapse } from '@florencelabs/collapse';
import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { createContext, useCallback, useContext, useId, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  ExpandableGroup context                                           */
/* ------------------------------------------------------------------ */

interface ExpandableGroupContextValue {
  /** Currently expanded key (single mode) or set of expanded keys (multiple mode). */
  expandedKeys: Set<string>;
  /** Register a toggle from a child Expandable. */
  toggle: (key: string, next: boolean) => void;
  /** Whether the group manages expand state on behalf of its children. */
  managed: boolean;
}

const ExpandableGroupContext = createContext<ExpandableGroupContextValue>({
  expandedKeys: new Set(),
  toggle: () => void 0,
  managed: false,
});

/* ------------------------------------------------------------------ */
/*  Expandable                                                        */
/* ------------------------------------------------------------------ */

export interface ExpandableProps {
  /** The header label displayed in the trigger row. */
  title: string;
  /** The content revealed when expanded. */
  children: ReactNode;
  /** Controlled expanded state. When provided the component is fully controlled. */
  isExpanded?: boolean;
  /** Callback fired when the user toggles the section. */
  onToggle?: (next: boolean) => void;
  /** Whether the section starts expanded in uncontrolled mode. @default false */
  defaultExpanded?: boolean;
  /** Additional class names applied to the outer wrapper. */
  className?: string;
  /** Additional class names applied to the trigger button row. */
  triggerClassName?: string;
  /** Additional class names applied to the content container. */
  contentClassName?: string;
  /** Additional class names applied to the title label. */
  titleClassName?: string;
  /** A prefix used to build `data-testid` attributes. */
  testIdPrefix?: string;
  /** Duration (seconds) for the expand / collapse animation. @default 0.3 */
  animationDuration?: number;
  /**
   * Unique key used by a managed `ExpandableGroup` to track which section is
   * open.  Required when the parent `ExpandableGroup` has `mode` set.
   * Falls back to `testIdPrefix` if omitted.
   */
  itemKey?: string;
}

export const Expandable = ({
  title,
  children,
  isExpanded: controlledExpanded,
  onToggle,
  defaultExpanded = false,
  className,
  triggerClassName,
  contentClassName,
  titleClassName,
  testIdPrefix = 'expandable',
  animationDuration = 0.3,
  itemKey,
}: ExpandableProps): ReactElement => {
  const group = useContext(ExpandableGroupContext);
  const resolvedKey = itemKey ?? testIdPrefix;

  const isControlled = controlledExpanded !== undefined;
  const isGroupManaged = group.managed && !isControlled;

  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const expanded = isControlled
    ? controlledExpanded
    : isGroupManaged
      ? group.expandedKeys.has(resolvedKey)
      : internalExpanded;

  const handleToggle = useCallback((): void => {
    const next = !expanded;

    if (isGroupManaged) {
      group.toggle(resolvedKey, next);
    } else if (!isControlled) {
      setInternalExpanded(next);
    }

    onToggle?.(next);
  }, [expanded, isControlled, isGroupManaged, group, resolvedKey, onToggle]);

  // Generate a unique panel id so the trigger can reference it via aria-controls.
  const panelId = useId();

  return (
    <div
      className={clsx('flex flex-col', className)}
      data-testid={`${testIdPrefix}-section`}
    >
      <button
        type={'button'}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={handleToggle}
        className={clsx(
          'flex w-full items-center justify-between px-0 py-l hover:cursor-pointer',
          triggerClassName,
        )}
        data-testid={`${testIdPrefix}-trigger`}
      >
        <span className={titleClassName}>{title}</span>
        <MaterialSymbol
          name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          size={24}
        />
      </button>

      <div id={panelId} data-testid={`${testIdPrefix}-content`}>
        <Collapse open={expanded} duration={animationDuration}>
          <div className={clsx('pb-l', contentClassName)}>{children}</div>
        </Collapse>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  ExpandableGroup                                                   */
/* ------------------------------------------------------------------ */

export interface ExpandableGroupProps {
  /** The sections to render. Each child should be an `<Expandable>` element. */
  children: ReactNode;
  /** Additional class names applied to the group wrapper. */
  className?: string;
  /** A prefix used to build `data-testid` attributes. */
  testIdPrefix?: string;
  /**
   * When set, the group manages expand / collapse state internally.
   *
   * - `"single"` — at most one section open at a time (accordion).
   * - `"multiple"` — any number of sections can be open simultaneously.
   *
   * Omit to keep the group as a layout-only wrapper (children manage their
   * own state or receive `isExpanded` / `onToggle` directly).
   */
  mode?: 'single' | 'multiple';
  /**
   * Keys of sections that should be expanded initially when `mode` is set.
   * Ignored in unmanaged mode. @default []
   */
  defaultExpandedKeys?: string[];
}

/**
 * Layout wrapper for a group of `<Expandable>` sections.
 *
 * When `mode` is provided the group manages expand / collapse state
 * internally via React context, eliminating the need for consumers to
 * wire `isExpanded` / `onToggle` on every child.
 *
 * Without `mode` the group is a convenience layout container with dividers
 * and a shared test-id — the caller is responsible for state management
 * (see the TwoColumnAccordionGroup story for a cross-group example).
 */
export const ExpandableGroup = ({
  children,
  className,
  testIdPrefix = 'expandable-group',
  mode,
  defaultExpandedKeys = [],
}: ExpandableGroupProps): ReactElement => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(defaultExpandedKeys),
  );

  const toggle = useCallback(
    (key: string, next: boolean): void => {
      setExpandedKeys((prev) => {
        const updated = new Set(mode === 'single' && next ? [] : prev);

        if (next) {
          updated.add(key);
        } else {
          updated.delete(key);
        }

        return updated;
      });
    },
    [mode],
  );

  const contextValue: ExpandableGroupContextValue = {
    expandedKeys,
    toggle,
    managed: mode !== undefined,
  };

  return (
    <ExpandableGroupContext.Provider value={contextValue}>
      <div
        className={clsx(
          'flex flex-col divide-y divide-neutral-200 border-t border-neutral-200',
          className,
        )}
        data-testid={`${testIdPrefix}-wrapper`}
        role={'region'}
        aria-label={'Expandable group'}
      >
        {children}
      </div>
    </ExpandableGroupContext.Provider>
  );
};
